<script setup lang="ts">
import logoMaintenance from '@/assets/images/logo/logo-luma-maintenance.png';
import { useAuthStore } from '@/stores/auth-store.ts';
import { ref, watch } from 'vue';

const authStore = useAuthStore();

// Track if modal has been shown in this session
const modalShownKey = 'maintenance-modal-shown';
const isModalOpen = ref(false);

// Check if we should show the modal
function checkAndShowModal() {
   // Only show if maintenance mode is active, user is authenticated, and modal hasn't been shown this session
   if (authStore.maintenanceMode && authStore.isAuthenticated) {
      const hasBeenShown = sessionStorage.getItem(modalShownKey);
      if (!hasBeenShown) {
         isModalOpen.value = true;
      }
   }
}

// Close modal and mark as shown for this session
function closeModal() {
   isModalOpen.value = false;
   sessionStorage.setItem(modalShownKey, 'true');
}

// Watch for auth state changes to show modal on login
watch(() => authStore.isAuthenticated, (isAuth) => {
   if (isAuth) {
      checkAndShowModal();
   }
   else {
      // Clear the flag when user logs out so it shows again on next login
      sessionStorage.removeItem(modalShownKey);
   }
}, { immediate: true });

// Also watch maintenance mode changes
watch(() => authStore.maintenanceMode, (maintenanceMode) => {
   if (maintenanceMode) {
      checkAndShowModal();
   }
});
</script>

<template>
   <Teleport to="body">
      <Transition
         enter-active-class="transition-opacity duration-300 ease-out"
         enter-from-class="opacity-0"
         enter-to-class="opacity-100"
         leave-active-class="transition-opacity duration-200 ease-in"
         leave-from-class="opacity-100"
         leave-to-class="opacity-0"
      >
         <div
            v-if="isModalOpen"
            class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="maintenance-modal-title"
            @click.self="closeModal"
         >
            <Transition
               enter-active-class="transition-all duration-300 ease-out"
               enter-from-class="opacity-0 scale-95"
               enter-to-class="opacity-100 scale-100"
               leave-active-class="transition-all duration-200 ease-in"
               leave-from-class="opacity-100 scale-100"
               leave-to-class="opacity-0 scale-95"
            >
               <div
                  v-if="isModalOpen"
                  class="relative bg-gradient-to-b from-[#D5F2E3] to-[#E8F8EF] rounded-[2rem] shadow-2xl max-w-2xl w-full mx-4 border-4 border-primary p-12"
               >
                  <!-- Close button -->
                  <button
                     type="button"
                     class="absolute top-4 right-4 w-12 h-12 bg-primary rounded-full flex items-center justify-center text-white hover:bg-primary-dark transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                     aria-label="Close maintenance notice"
                     @click="closeModal"
                  >
                     <svg
                        class="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                     >
                        <path
                           stroke-linecap="round"
                           stroke-linejoin="round"
                           stroke-width="2.5"
                           d="M6 18L18 6M6 6l12 12"
                        />
                     </svg>
                  </button>

                  <!-- Content -->
                  <div class="flex flex-col items-center text-center space-y-6">
                     <!-- Logo -->
                     <div class="w-32 h-32 flex items-center justify-center">
                        <img
                           :src="logoMaintenance"
                           alt="Luma Maintenance"
                           class="w-full h-full object-contain"
                        >
                     </div>

                     <!-- Heading -->
                     <h2
                        id="maintenance-modal-title"
                        class="text-3xl md:text-4xl font-bold text-primary-dark"
                     >
                        We're performing<br>maintenance
                     </h2>

                     <!-- Body text -->
                     <div class="space-y-3 max-w-xl">
                        <p class="text-lg text-gray-800">
                           Apaluma is temporarily offline while we make system updates.
                           <span class="font-bold">In the meantime, you can access our unified map</span>
                        </p>

                        <!-- Italic text -->
                        <p class="text-base italic text-gray-700">
                           We'll be back soon! Thank you for your patience.
                        </p>
                     </div>
                  </div>
               </div>
            </Transition>
         </div>
      </Transition>
   </Teleport>
</template>

<style scoped>
/* Add any additional styles if needed */
</style>
